import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as Form from "@radix-ui/react-form";
import { Text, Heading, Button, Flex } from "@radix-ui/themes";
import { toast } from "react-toastify";
import { CloseIcon } from "@/assets/icons";
import { IconButton } from "@/components/ui/IconButton";
import ControlledInputField from "@/components/ui/form/ControlledInputField";
import { useAuthControllerForgotPassword } from "@/lib/lutely-api/gen/auth/auth";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email." }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordFormProps = {
  onSuccess: () => void;
  onBackToLogin: () => void;
  compact?: boolean;
};

export function ForgotPasswordForm({
  onSuccess,
  onBackToLogin,
  compact,
}: ForgotPasswordFormProps) {
  const [loading, setLoading] = useState(false);

  const { handleSubmit, resetField, control } =
    useForm<ForgotPasswordFormValues>({
      resolver: zodResolver(forgotPasswordSchema),
      mode: "onBlur",
      reValidateMode: "onChange",
      defaultValues: { email: "" },
    });

  const { mutateAsync: sendForgotPasswordCode } =
    useAuthControllerForgotPassword();

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setLoading(true);
    try {
      await sendForgotPasswordCode({ data: { email: data.email } });
      toast.success("A password reset link has been sent to your email.");
      resetField("email");
      onSuccess();
    } catch {
      toast.error(
        "Something went wrong. Please reach out to support and we'll fix it.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      aria-labelledby="forgot-password-form-title"
      direction="column"
      className={compact ? "gap-4" : "gap-6 sm:gap-7"}
    >
      <div className="space-y-3 text-center sm:text-left">
        <Heading
          id="forgot-password-form-title"
          as="h2"
          weight="bold"
          className={`font-figtree leading-[1.15] ${compact ? "text-[24px] tracking-[-0.24px]" : "text-[30px] tracking-[-0.3px] sm:text-[36px] sm:tracking-[-0.36px]"}`}
        >
          Forgot your password?
        </Heading>
        <Text
          size="2"
          className="text-gray-700 text-[14px] leading-[1.45] sm:text-[15px]"
        >
          Enter the email linked to your account and we'll send a one-time code
          to reset your password.
        </Text>
      </div>

      <Form.Root
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={`flex flex-col ${compact ? "gap-4" : "gap-6 sm:gap-7"}`}
      >
        <div className="space-y-4">
          <ControlledInputField
            name="email"
            control={control}
            label="Email"
            type="email"
            placeholder="you@example.com"
            required
            endAdornment={
              <IconButton
                size="sm"
                aria-label="Clear email"
                type="button"
                onClick={() => resetField("email")}
              >
                <CloseIcon />
              </IconButton>
            }
          />
        </div>

        <Form.Submit asChild>
          <Button type="submit" loading={loading} radius="full">
            {loading ? "Sending..." : "Reset Password"}
          </Button>
        </Form.Submit>

        <div className="text-center text-sm text-gray-700">
          <button
            type="button"
            onClick={onBackToLogin}
            className="font-semibold hover:underline cursor-pointer"
          >
            Back to sign in
          </button>
        </div>
      </Form.Root>
    </Flex>
  );
}

export default ForgotPasswordForm;
